import AppBarSpacer from '../components/common/AppBarSpacer';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportRowsToXlsx } from '../utils/xlsxExport';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UserInfoDialog from '../components/FormDialog/UserInfoDialog';
import { User } from '../types/UserTable.type';
import { useEffect, useState } from 'react';

import AssessmentMiniDrawer from '../components/AssessmentMiniDrawer';
import GenderChip from '../components/common/GenderChip';
import SofLevelChip from '../components/common/SofLevelChip';
import { EmptyValue, isParentEmpty } from '../components/common/EmptyValue';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
} from '../components/common/ManagementTable';
import PageHeader from '../components/common/PageHeader';
import MiniDrawer from '../components/MiniDrawer';
import CoursePackageTimeline from '../components/CoursePackageTimeline';
import { useIsMobile } from '../hooks/useResponsive';
import type {
  CoachMasterProgram,
  CoachMasterLevel,
  CoachMasterStudent,
  CoachMasterStudentsResponse,
} from '../types/CoachMasterData.type';
import { apiGet, apiPost, apiPut } from '../utils/apiClient';
import { notifyAPIClient, notifyFailure } from '../utils/toast';
import { expiredRowBg, rowHoverBg } from '../utils/rowHighlight';
import { useTranslation } from '../i18n';

const programs: Array<{
  labelKey: string;
  value: CoachMasterProgram | 'ARCHIVE';
}> = [
  { labelKey: 'coachMasterData.tabSof', value: 'SOF' },
  { labelKey: 'coachMasterData.tabFlyingFish', value: 'FLYING FISH' },
  { labelKey: 'coachMasterData.tabAdultCourse', value: 'ADULT COURSES' },
  { labelKey: 'coachMasterData.tabPrivateLesson', value: 'PRIVATE LESSON' },
  { labelKey: 'coachMasterData.tabMembership', value: 'MEMBERSHIP' },
  { labelKey: 'coachMasterData.tabCombo10', value: 'COMBO 10' },
  { labelKey: 'coachMasterData.tabArchive', value: 'ARCHIVE' },
];

const isTicketProgram = (
  value: CoachMasterProgram | 'ARCHIVE',
): value is 'MEMBERSHIP' | 'COMBO 10' =>
  value === 'MEMBERSHIP' || value === 'COMBO 10';

const formatDob = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${date.getFullYear()}`;
};

const formatRegistrationDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

const isRegistrationExpired = (value: string): boolean => {
  const expiryDate = new Date(value);
  return !Number.isNaN(expiryDate.getTime()) && expiryDate < new Date();
};

type CoachMasterDataProps = {
  isAdmin?: boolean;
};

const CoachMasterData = ({ isAdmin = false }: CoachMasterDataProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('All');
  const [courseFilter, setCourseFilter] = useState('All');
  const [ticketFilter, setTicketFilter] = useState('All');
  const [archiveServiceType, setArchiveServiceType] = useState<
    CoachMasterProgram | 'All'
  >('All');
  const [availableCourses, setAvailableCourses] = useState<string[]>([]);
  const [availableTickets, setAvailableTickets] = useState<string[]>([]);
  const [availableLevelNames, setAvailableLevelNames] = useState<string[]>([]);
  const [availableLevels, setAvailableLevels] = useState<CoachMasterLevel[]>(
    [],
  );
  const [students, setStudents] = useState<CoachMasterStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [selectedCourse, setSelectedCourse] =
    useState<CoachMasterStudent | null>(null);
  const [archiveStudent, setArchiveStudent] =
    useState<CoachMasterStudent | null>(null);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [unarchiveStudent, setUnarchiveStudent] =
    useState<CoachMasterStudent | null>(null);
  const [unarchiveSubmitting, setUnarchiveSubmitting] = useState(false);
  const [levelStudent, setLevelStudent] = useState<CoachMasterStudent | null>(
    null,
  );
  const [levelOptions, setLevelOptions] = useState<CoachMasterLevel[]>([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [levelSubmitting, setLevelSubmitting] = useState(false);

  // View / edit a student's full profile (fetched by user id).
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userInfoOpen, setUserInfoOpen] = useState(false);
  const [userInfoViewOnly, setUserInfoViewOnly] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const openStudentProfile = async (studentId: number, viewOnly: boolean) => {
    try {
      const res = await apiGet<{ data: User }>(`/user/info/${studentId}`);
      setSelectedUser(res.data);
      setUserInfoViewOnly(viewOnly);
      if (!viewOnly) {
        localStorage.setItem('selectedUserType', res.data.userType);
      }
      setUserInfoOpen(true);
    } catch {
      notifyFailure();
    }
  };

  const visiblePrograms = programs;
  const activeProgram = visiblePrograms[activeTab].value;
  const activeTicketProgram = isTicketProgram(activeProgram);
  const archiveServiceTypeIsTicket =
    activeProgram === 'ARCHIVE' &&
    archiveServiceType !== 'All' &&
    isTicketProgram(archiveServiceType);
  const showLevelFilter = !activeTicketProgram && !archiveServiceTypeIsTicket;

  useEffect(() => {
    let mounted = true;

    if (activeProgram === 'ARCHIVE') {
      setAvailableLevels([]);
      setAvailableCourses([]);
      setAvailableTickets([]);
      setCourseFilter('All');
      setTicketFilter('All');
      setAvailableLevelNames([]);
      setLevelFilter('All');
      setArchiveServiceType('All');
      return () => {
        mounted = false;
      };
    }

    setAvailableCourses([]);
    setAvailableTickets([]);
    setAvailableLevelNames([]);
    setCourseFilter('All');
    setTicketFilter('All');
    setArchiveServiceType('All');

    if (isTicketProgram(activeProgram)) {
      setAvailableLevels([]);
      setLevelFilter('All');
      return () => {
        mounted = false;
      };
    }

    const fetchLevels = async () => {
      try {
        const response = await apiGet<{ data: CoachMasterLevel[] }>(
          `/course/coach-levels?program=${encodeURIComponent(activeProgram)}`,
        );

        if (mounted) {
          setAvailableLevels(response.data || []);
          setLevelFilter('All');
        }
      } catch {
        if (mounted) {
          setAvailableLevels([]);
          notifyFailure();
        }
      }
    };

    void fetchLevels();
    return () => {
      mounted = false;
    };
  }, [activeProgram]);

  useEffect(() => {
    let mounted = true;
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          program: activeProgram,
          page: String(page + 1),
          limit: String(rowsPerPage),
          search,
          level: levelFilter === 'All' ? '' : levelFilter,
          course: courseFilter === 'All' ? '' : courseFilter,
          archiveServiceType:
            activeProgram === 'ARCHIVE' && archiveServiceType !== 'All'
              ? archiveServiceType
              : '',
          ticket:
            activeTicketProgram && ticketFilter !== 'All' ? ticketFilter : '',
        });
        const response = await apiGet<CoachMasterStudentsResponse>(
          `/registration/coach-master/students?${params.toString()}`,
        );

        if (mounted) {
          setStudents(response.data || []);
          setTotalItems(response.metadata?.totalItems || 0);
          setAvailableCourses(response.metadata?.availableCourses || []);
          setAvailableTickets(response.metadata?.availableTickets || []);
          if (activeProgram === 'ARCHIVE') {
            setAvailableLevelNames(response.metadata?.availableLevels || []);
          } else {
            setAvailableLevelNames([]);
          }
          setLastSyncTime(new Date());
        }
      } catch {
        if (mounted) notifyFailure();
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void fetchStudents();
    return () => {
      mounted = false;
    };
  }, [
    activeProgram,
    activeTicketProgram,
    courseFilter,
    isAdmin,
    levelFilter,
    page,
    rowsPerPage,
    search,
    ticketFilter,
    archiveServiceType,
    reloadToken,
  ]);

  const handleTabChange = (_event: React.SyntheticEvent, nextTab: number) => {
    setActiveTab(nextTab);
    setPage(0);
    setSearch('');
    setLevelFilter('All');
    setCourseFilter('All');
    setTicketFilter('All');
    setArchiveServiceType('All');
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  // Export the whole current view (active program + level/course/search filters),
  // not just the visible page, to an Excel file.
  const handleDownloadStudents = async () => {
    if (totalItems === 0) return;
    setDownloadLoading(true);
    try {
      const params = new URLSearchParams({
        program: activeProgram,
        page: '1',
        limit: '1',
        export: 'true',
        search,
        level: levelFilter === 'All' ? '' : levelFilter,
        course: courseFilter === 'All' ? '' : courseFilter,
        archiveServiceType:
          activeProgram === 'ARCHIVE' && archiveServiceType !== 'All'
            ? archiveServiceType
            : '',
        ticket:
          activeTicketProgram && ticketFilter !== 'All' ? ticketFilter : '',
      });
      const response = await apiGet<CoachMasterStudentsResponse>(
        `/registration/coach-master/students?${params.toString()}`,
      );
      const rows = (response.data || []).map((student) => ({
        [t('coachMasterData.colName')]: student.name,
        [t('coachMasterData.colFirstName')]: student.firstName,
        [t('coachMasterData.colLastName')]: student.lastName,
        [t('coachMasterData.colAge')]: student.age,
        [t('coachMasterData.colDob')]: formatDob(student.dob),
        [t('coachMasterData.colGender')]: student.gender,
        [t('coachMasterData.colParentName')]: student.parentName || '',
        [t('coachMasterData.colEmail')]: student.email || '',
        [t('coachMasterData.colPhone')]: student.phone || '',
        [t('coachMasterData.colCourse')]: student.course || '',
        [t('coachMasterData.colLevel')]: student.level || '',
      }));
      const stamp = new Date().toISOString().slice(0, 10);
      exportRowsToXlsx(
        rows,
        `master_data_${activeProgram.replace(/\s+/g, '_').toLowerCase()}_${stamp}.xlsx`,
        String(Date.now()),
      );
    } catch (error) {
      console.error('Failed to download master data:', error);
      notifyFailure();
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleOpenCourse = (
    event: React.MouseEvent,
    student: CoachMasterStudent,
  ) => {
    event.stopPropagation();
    setSelectedCourse(student);
  };

  const handleConfirmArchive = async () => {
    if (!archiveStudent) return;

    try {
      setArchiveSubmitting(true);
      const response = await apiPost<
        { message?: string },
        Record<string, never>
      >(
        `/registration/coach-master/students/${archiveStudent.registrationId}/archive`,
        {},
      );
      notifyAPIClient(response.message || t('coachMasterData.archiveSuccess'));
      setStudents((current) =>
        current.filter(
          (student) => student.registrationId !== archiveStudent.registrationId,
        ),
      );
      setTotalItems((current) => Math.max(current - 1, 0));
      if (students.length === 1 && page > 0) {
        setPage((current) => current - 1);
      }
      setArchiveStudent(null);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      notifyAPIClient(message || t('coachMasterData.archiveError'));
    } finally {
      setArchiveSubmitting(false);
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!unarchiveStudent) return;

    try {
      setUnarchiveSubmitting(true);
      const response = await apiPost<
        { message?: string },
        Record<string, never>
      >(
        `/registration/coach-master/students/${unarchiveStudent.registrationId}/unarchive`,
        {},
      );
      notifyAPIClient(
        response.message || t('coachMasterData.unarchiveSuccess'),
      );
      setStudents((current) =>
        current.filter(
          (student) =>
            student.registrationId !== unarchiveStudent.registrationId,
        ),
      );
      setTotalItems((current) => Math.max(current - 1, 0));
      if (students.length === 1 && page > 0) {
        setPage((current) => current - 1);
      }
      setUnarchiveStudent(null);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      notifyAPIClient(message || t('coachMasterData.unarchiveError'));
    } finally {
      setUnarchiveSubmitting(false);
    }
  };

  const openLevelEditor = async (student: CoachMasterStudent) => {
    try {
      const response = await apiGet<{ data: CoachMasterLevel[] }>(
        `/course/get-levels/${student.subsubtypeId}`,
      );
      setLevelOptions(response.data || []);
      setSelectedLevel(student.level || '');
      setLevelStudent(student);
    } catch {
      notifyFailure();
    }
  };

  const saveLevel = async () => {
    if (!levelStudent || !selectedLevel) return;
    const selected = levelOptions.find(
      (item) => item.levelName === selectedLevel,
    );
    if (!selected) return;
    try {
      setLevelSubmitting(true);
      const response = await apiPut<
        { data: { levelName: string }; message?: string },
        { subSubTypeId: number; level: number; comment: string }
      >(`/course/registration-level/${levelStudent.registrationId}`, {
        subSubTypeId: levelStudent.subsubtypeId,
        level: selected.level,
        comment: 'Admin correction from Master Data',
      });
      setStudents((current) =>
        current.map((item) =>
          item.registrationId === levelStudent.registrationId
            ? { ...item, level: response.data.levelName }
            : item,
        ),
      );
      setLastSyncTime(new Date());
      notifyAPIClient(response.message || 'Level updated successfully.');
      setLevelStudent(null);
    } catch {
      notifyFailure();
    } finally {
      setLevelSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen">
      {isAdmin ? (
        <MiniDrawer open={open} setOpen={setOpen} />
      ) : (
        <AssessmentMiniDrawer open={open} setOpen={setOpen} />
      )}

      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title={t('coachMasterData.title')}
            subtitle={
              isAdmin
                ? t('coachMasterData.subtitleAdmin')
                : t('coachMasterData.subtitleView')
            }
          />

          <Card elevation={2}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                sx={{ px: 1.5, borderBottom: '1px solid #e0e0e0' }}
              >
                {visiblePrograms.map((program) => (
                  <Tab key={program.value} label={t(program.labelKey)} />
                ))}
              </Tabs>

              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    p: 1.5,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('coachMasterData.studentsCount', {
                      label: t(visiblePrograms[activeTab].labelKey),
                      count: totalItems,
                    })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <TextField
                      size="small"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(0);
                      }}
                      placeholder={t('coachMasterData.searchPlaceholder')}
                      inputProps={{
                        'aria-label': t('coachMasterData.searchAriaLabel'),
                      }}
                      sx={{ width: 320, maxWidth: '100%' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {activeProgram === 'ARCHIVE' && (
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel id="master-archive-service-type-label">
                          {t('coachMasterData.serviceType')}
                        </InputLabel>
                        <Select
                          labelId="master-archive-service-type-label"
                          value={archiveServiceType}
                          label={t('coachMasterData.serviceType')}
                          onChange={(event) => {
                            setArchiveServiceType(
                              event.target.value as CoachMasterProgram | 'All',
                            );
                            setLevelFilter('All');
                            setPage(0);
                          }}
                        >
                          <MenuItem value="All">
                            {t('coachMasterData.allServiceTypes')}
                          </MenuItem>
                          {programs
                            .filter((program) => program.value !== 'ARCHIVE')
                            .map((program) => (
                              <MenuItem
                                key={program.value}
                                value={program.value}
                              >
                                {t(program.labelKey)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                    {showLevelFilter && (
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="master-level-filter-label">
                          {t('coachMasterData.level')}
                        </InputLabel>
                        <Select
                          labelId="master-level-filter-label"
                          value={levelFilter}
                          label={t('coachMasterData.level')}
                          onChange={(event) => {
                            setLevelFilter(event.target.value);
                            setPage(0);
                          }}
                        >
                          <MenuItem value="All">
                            {t('coachMasterData.allLevels')}
                          </MenuItem>
                          {activeProgram === 'ARCHIVE'
                            ? availableLevelNames.map((levelName) => (
                                <MenuItem key={levelName} value={levelName}>
                                  {levelName}
                                </MenuItem>
                              ))
                            : availableLevels.map((level) => (
                                <MenuItem
                                  key={level.level}
                                  value={level.levelName}
                                >
                                  {level.levelName}
                                </MenuItem>
                              ))}
                        </Select>
                      </FormControl>
                    )}
                    {activeTicketProgram && (
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel id="master-ticket-filter-label">
                          {t('coachMasterData.ticket')}
                        </InputLabel>
                        <Select
                          labelId="master-ticket-filter-label"
                          value={ticketFilter}
                          label={t('coachMasterData.ticket')}
                          onChange={(event) => {
                            setTicketFilter(event.target.value);
                            setPage(0);
                          }}
                        >
                          <MenuItem value="All">
                            {t('coachMasterData.allTickets')}
                          </MenuItem>
                          {availableTickets.map((ticket) => (
                            <MenuItem key={ticket} value={ticket}>
                              {ticket} Packages
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <FormControl size="small" sx={{ minWidth: 230 }}>
                      <InputLabel id="master-course-filter-label">
                        {t(
                          activeTicketProgram
                            ? 'coachMasterData.package'
                            : 'coachMasterData.course',
                        )}
                      </InputLabel>
                      <Select
                        labelId="master-course-filter-label"
                        value={courseFilter}
                        label={t(
                          activeTicketProgram
                            ? 'coachMasterData.package'
                            : 'coachMasterData.course',
                        )}
                        onChange={(event) => {
                          setCourseFilter(event.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="All">
                          {t(
                            activeTicketProgram
                              ? 'coachMasterData.allPackages'
                              : 'coachMasterData.allCourses',
                          )}
                        </MenuItem>
                        {availableCourses.map((course) => (
                          <MenuItem key={course} value={course}>
                            {course}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title={t('coachMasterData.download')}>
                      <span>
                        <IconButton
                          color="primary"
                          aria-label={t('coachMasterData.download')}
                          onClick={handleDownloadStudents}
                          disabled={downloadLoading || totalItems === 0}
                        >
                          {downloadLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <FileDownloadIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <TableContainer component={Paper} elevation={0}>
                  <Table
                    size="small"
                    padding="checkbox"
                    aria-label={t('coachMasterData.tableAriaLabel')}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                        }}
                      >
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colUserId')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colAvatar')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colName')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colFirstName')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colLastName')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colAge')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colDob')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colGender')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colParentName')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colEmail')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colPhone')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colCourse')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('coachMasterData.colLevel')}
                        </ManagementTableHeaderCell>
                        {isAdmin && (
                          <ManagementTableHeaderCell sticky>
                            {t('coachMasterData.colActions')}
                          </ManagementTableHeaderCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell
                            colSpan={isAdmin ? 14 : 13}
                            align="center"
                            sx={{ py: 5 }}
                          >
                            <CircularProgress size={28} />
                          </TableCell>
                        </TableRow>
                      ) : students.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={isAdmin ? 14 : 13}
                            align="center"
                            sx={{ py: 5 }}
                          >
                            {t('coachMasterData.noStudents')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        students.map((student) => (
                          <TableRow
                            key={`${student.registrationId}-${student.id}`}
                            hover
                            sx={
                              isRegistrationExpired(student.registrationEndDate)
                                ? {
                                    backgroundColor: expiredRowBg,
                                    '&:hover': {
                                      backgroundColor: expiredRowBg,
                                    },
                                  }
                                : {
                                    '&:hover': {
                                      backgroundColor: rowHoverBg,
                                    },
                                  }
                            }
                          >
                            <TableCell>{student.id}</TableCell>
                            <TableCell>
                              <Avatar
                                src={student.pictureUrl || undefined}
                                alt={student.name}
                                sx={{ width: 32, height: 32 }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              {student.name}
                            </TableCell>
                            <TableCell>{student.firstName}</TableCell>
                            <TableCell>{student.lastName}</TableCell>
                            <TableCell>{student.age}</TableCell>
                            <TableCell>{formatDob(student.dob)}</TableCell>
                            <TableCell>
                              <GenderChip value={student.gender} />
                            </TableCell>
                            <TableCell>
                              {isParentEmpty(student.parentName) ? (
                                <EmptyValue />
                              ) : (
                                student.parentName
                              )}
                            </TableCell>
                            <TableCell>
                              {student.email ? (
                                <Tooltip title={t('coachMasterData.openGmail')}>
                                  <a
                                    href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(student.email)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      textDecoration: 'underline',
                                      color: 'inherit',
                                    }}
                                  >
                                    {student.email}
                                  </a>
                                </Tooltip>
                              ) : (
                                <span style={{ opacity: 0.4 }}>
                                  {t('coachMasterData.none')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {student.phone ? (
                                student.phone === '0909999999' ? (
                                  <span style={{ opacity: 0.4 }}>
                                    {student.phone}
                                  </span>
                                ) : (
                                  <Tooltip
                                    title={t('coachMasterData.openZalo')}
                                  >
                                    <a
                                      href={`https://zalo.me/${encodeURIComponent(student.phone)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        textDecoration: 'underline',
                                        color: 'inherit',
                                      }}
                                    >
                                      {student.phone}
                                    </a>
                                  </Tooltip>
                                )
                              ) : (
                                <span style={{ opacity: 0.4 }}>
                                  {t('coachMasterData.none')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <ButtonBase
                                onClick={(event) =>
                                  handleOpenCourse(event, student)
                                }
                                aria-label={t(
                                  'coachMasterData.viewCourseInfo',
                                  {
                                    name: student.name,
                                  },
                                )}
                                sx={{
                                  minWidth: 0,
                                  p: 0,
                                  textDecoration: 'underline',
                                  borderRadius: 0,
                                  justifyContent: 'flex-start',
                                  color: 'inherit',
                                  font: 'inherit',
                                  '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: 'transparent',
                                  },
                                  '&:hover .course-text': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {student.course ? (
                                  <Typography
                                    component="span"
                                    className="course-text "
                                    variant="body2"
                                    sx={{
                                      color: 'inherit',
                                      textAlign: 'left',
                                    }}
                                  >
                                    {student.course}
                                  </Typography>
                                ) : (
                                  <EmptyValue />
                                )}
                              </ButtonBase>
                            </TableCell>
                            <TableCell>
                              {isAdmin && activeProgram !== 'ARCHIVE' ? (
                                <ButtonBase
                                  onClick={() => void openLevelEditor(student)}
                                  sx={{ borderRadius: 1 }}
                                  aria-label={`Edit level for ${student.name}`}
                                >
                                  <SofLevelChip
                                    level={student.level}
                                    serviceType={activeProgram}
                                  />
                                </ButtonBase>
                              ) : (
                                <SofLevelChip
                                  level={student.level}
                                  serviceType={
                                    activeProgram === 'ARCHIVE'
                                      ? undefined
                                      : activeProgram
                                  }
                                />
                              )}
                            </TableCell>
                            {isAdmin && (
                              <ManagementTableActionCell>
                                <Tooltip title={t('coachMasterData.view')}>
                                  <IconButton
                                    color="primary"
                                    aria-label={t('coachMasterData.view')}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void openStudentProfile(student.id, true);
                                    }}
                                  >
                                    <VisibilityOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('coachMasterData.edit')}>
                                  <IconButton
                                    color="primary"
                                    aria-label={t('coachMasterData.edit')}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void openStudentProfile(
                                        student.id,
                                        false,
                                      );
                                    }}
                                  >
                                    <EditOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                                {activeProgram === 'ARCHIVE' ? (
                                  <Tooltip
                                    title={t(
                                      'coachMasterData.unarchiveStudentTooltip',
                                    )}
                                  >
                                    <IconButton
                                      color="success"
                                      aria-label={t(
                                        'coachMasterData.unarchiveStudentAria',
                                        { name: student.name },
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setUnarchiveStudent(student);
                                      }}
                                    >
                                      <UnarchiveIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    title={t(
                                      'coachMasterData.archiveStudentTooltip',
                                    )}
                                  >
                                    <IconButton
                                      color="warning"
                                      aria-label={t(
                                        'coachMasterData.archiveStudentAria',
                                        { name: student.name },
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setArchiveStudent(student);
                                      }}
                                    >
                                      <ArchiveIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </ManagementTableActionCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: 1.5,
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('coachMasterData.lastSync')}{' '}
                    {lastSyncTime
                      ? lastSyncTime.toLocaleString('en-GB')
                      : '---'}
                  </Typography>
                  <TablePagination
                    component="div"
                    count={totalItems}
                    page={page}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    labelRowsPerPage={t('coachMasterData.rowsPerPage')}
                  />
                </Box>
              </>
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog
        open={Boolean(selectedCourse)}
        onClose={() => setSelectedCourse(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            minHeight: { xs: 'auto', sm: 480 },
            maxHeight: 'calc(100vh - 48px)',
          },
        }}
      >
        <DialogTitle>{t('coachMasterData.courseInformation')}</DialogTitle>
        <DialogContent dividers>
          {selectedCourse && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('coachMasterData.student')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedCourse.name}
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table
                  size="small"
                  padding="checkbox"
                  aria-label="Student course packages"
                >
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                    >
                      <ManagementTableHeaderCell>
                        {t('coachMasterData.colCourse')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('coachMasterData.colRegStartDate')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('coachMasterData.colExpiryDate')}
                      </ManagementTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCourse.packages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                          {t('coachMasterData.noPackageInfo')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedCourse.packages.map((coursePackage) => (
                        <TableRow
                          key={coursePackage.id}
                          sx={{ '&:hover': { backgroundColor: rowHoverBg } }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {coursePackage.course}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatRegistrationDate(
                              coursePackage.registrationStartDate,
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatRegistrationDate(
                              coursePackage.registrationEndDate,
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedCourse.packages.length > 0 && (
                <CoursePackageTimeline packages={selectedCourse.packages} />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCourse(null)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(levelStudent)}
        onClose={() => setLevelStudent(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit student level</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {levelStudent?.name} · Registration #{levelStudent?.registrationId}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="master-level-edit-label">Level</InputLabel>
            <Select
              labelId="master-level-edit-label"
              value={selectedLevel}
              label="Level"
              onChange={(event) => setSelectedLevel(event.target.value)}
            >
              {levelOptions.map((item) => (
                <MenuItem key={item.level} value={item.levelName}>
                  {item.levelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {levelStudent &&
            levelOptions.findIndex((item) => item.levelName === selectedLevel) <
              levelOptions.findIndex(
                (item) => item.levelName === levelStudent.level,
              ) && (
              <Typography color="warning.main" variant="body2" mt={1}>
                This is a level-down change and will be recorded in the level
                history.
              </Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLevelStudent(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void saveLevel()}
            disabled={levelSubmitting || !selectedLevel}
          >
            {levelSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Save level'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(archiveStudent)}
        onClose={() => !archiveSubmitting && setArchiveStudent(null)}
      >
        <DialogTitle>{t('coachMasterData.archiveDialogTitle')}</DialogTitle>
        <DialogContent>
          {t('coachMasterData.archiveConfirm', {
            name: archiveStudent?.name || t('coachMasterData.thisStudent'),
          })}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setArchiveStudent(null)}
            disabled={archiveSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="warning"
            onClick={() => void handleConfirmArchive()}
            disabled={archiveSubmitting}
          >
            {archiveSubmitting
              ? t('coachMasterData.archiving')
              : t('coachMasterData.archive')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(unarchiveStudent)}
        onClose={() => !unarchiveSubmitting && setUnarchiveStudent(null)}
      >
        <DialogTitle>{t('coachMasterData.unarchiveDialogTitle')}</DialogTitle>
        <DialogContent>
          {t('coachMasterData.unarchiveConfirm', {
            name: unarchiveStudent?.name || t('coachMasterData.thisStudent'),
          })}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnarchiveStudent(null)}
            disabled={unarchiveSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="success"
            onClick={() => void handleConfirmUnarchive()}
            disabled={unarchiveSubmitting}
          >
            {unarchiveSubmitting
              ? t('coachMasterData.unarchiving')
              : t('coachMasterData.unarchive')}
          </Button>
        </DialogActions>
      </Dialog>

      <UserInfoDialog
        open={userInfoOpen}
        handleClose={() => setUserInfoOpen(false)}
        user={selectedUser}
        fetchUsers={() => setReloadToken((token) => token + 1)}
        viewOnly={userInfoViewOnly}
      />
    </div>
  );
};

export default CoachMasterData;
